import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { getBaseSchemaType, getImportOrder } from "./schemas";
import { buildFieldDefinitions, hasEnrichments } from "./fmxFieldMetadata";
import { parseCSV, buildMappedRows, computeCellErrors, downloadCSV, suggestMapping, latestImportPerSchema, importRowsCount } from "./utils";
import { tableFromWorksheet } from "./sheetParse";
import { C, T } from "./theme";
import { supabase } from "./supabase";
import { getMappingSuggestions, getSavedRulesForSchema, getProjectImports, getImportRows, getAllDependencyCaches, saveFmxReferenceCache, getCurrentProfile, getAllProfiles, getActivePrompt, getEnabledExamplesForPrompt, createExtractionRun, completeExtractionRun, recordCorrections, incrementExampleUsage, getFieldOverrides, hasFeature, listCachedEquipmentIds } from "./db";
import { buildSystemPrompt, extractUsage } from "./promptTemplates";
import UserMenu from "./components/UserMenu";
import ProfileEditModal from "./components/ProfileEditModal";
import { AppShell, TopBar, ContentHeader, Button as KitButton, Badge as KitBadge } from "./components/fmxkit";
import { WizardHeaderBand, WizardCompactBar } from "./components/WizardChrome";
import MigSidebar, { visibleTools } from "./components/MigSidebar";
import MigrationTab from "./components/MigrationTab";
import { syncFmxDataForProject, fetchAllDependencies, getDepKeysForSchema, normalizeModules, syncSchedulingRateTypes } from "./fmxSync";
import { getFieldTypeCategory } from "./fmxFieldTypes";
import { claudeFetch, parseClaudeText } from "./apiClient";
import { extractPdfToSheet } from "./pdfExtract";
import MapRuleModal from "./components/MapRuleModal";
import ProjectScreen from "./components/ProjectScreen";
import DependenciesView from "./components/DependenciesView";
import ProjectSettingsView from "./components/ProjectSettingsView";
import AttachmentFieldScan from "./components/scan/AttachmentFieldScan";
import QbrScreen from "./components/QbrScreen";
import FieldBuilderTool from "./components/fieldBuilder/FieldBuilderTool";
import StepUpload from "./components/StepUpload";
import StepMapFields from "./components/StepMapFields";
import StepValidate from "./components/StepValidate";
import StepExport from "./components/StepExport";
import AuthScreen from "./components/AuthScreen";

const GLOBAL_STYLES = `
  *, *::before, *::after { box-sizing: border-box; }
  body { margin: 0; }
  input, select, textarea, button { font-family: system-ui, -apple-system, sans-serif; }
  .fmx-btn-primary {
    background: ${C.orange}; color: ${C.white}; border: none; border-radius: 6px;
    padding: 8px 20px; cursor: pointer; font-size: 14px; font-weight: 500;
    transition: all 0.15s ease;
  }
  .fmx-btn-primary:hover:not(:disabled) { background: ${C.orangeHov}; }
  .fmx-btn-primary:disabled { background: ${C.border}; color: ${C.textLight}; cursor: not-allowed; }
  .fmx-btn-secondary {
    background: ${C.white}; color: ${C.orange}; border: 1px solid ${C.orange};
    border-radius: 6px; padding: 8px 20px; cursor: pointer; font-size: 14px;
    font-weight: 500; transition: all 0.15s ease;
  }
  .fmx-btn-secondary:hover { background: #FFF5F2; }
  .fmx-btn-nav-back {
    background: ${C.white}; color: ${C.navy}; border: 1px solid ${C.navy};
    border-radius: 6px; padding: 8px 20px; cursor: pointer; font-size: 14px;
    font-weight: 500; transition: all 0.15s ease;
  }
  .fmx-btn-nav-back:hover { background: ${C.navyTint}; }
  .fmx-btn-destructive {
    background: ${C.white}; color: #888; border: 1px solid #888;
    border-radius: 6px; padding: 4px 10px; cursor: pointer; font-size: 13px;
    transition: all 0.15s ease;
  }
  .fmx-btn-destructive:hover { background: ${C.bgPage}; }
  .fmx-btn-xs {
    background: ${C.white}; color: ${C.textMid}; border: 1px solid ${C.border};
    border-radius: 6px; padding: 3px 8px; cursor: pointer; font-size: 11px;
    transition: all 0.15s ease; white-space: nowrap;
  }
  .fmx-btn-xs:hover { background: ${C.bgPage}; }
  .fmx-btn-xs-rule {
    background: ${C.white}; color: ${C.orange}; border: 1px solid ${C.orange};
    border-radius: 6px; padding: 3px 8px; cursor: pointer; font-size: 11px;
    transition: all 0.15s ease; white-space: nowrap;
  }
  .fmx-btn-xs-rule:hover { background: #FFF5F2; }
  .fmx-btn-xs-rule.active {
    background: ${C.errBg}; color: ${C.errText}; border-color: ${C.errBorder};
  }
  .fmx-btn-xs-rule.active:hover { background: #FFE5E5; }
  .fmx-input {
    font-size: 13px; padding: 6px 10px; border-radius: 6px; border: 1px solid ${C.border};
    background: ${C.white}; color: ${C.textDark}; outline: none;
    transition: border-color 0.15s ease;
  }
  .fmx-input:focus { border-color: ${C.blue}; }
  select.fmx-select {
    font-size: 12px; padding: 4px 8px; border-radius: 6px; border: 1px solid ${C.border};
    background: ${C.white}; color: ${C.textDark}; width: 100%;
  }
  textarea.fmx-textarea {
    font-size: 12px; padding: 8px; border-radius: 6px; border: 1px solid ${C.border};
    background: ${C.white}; color: ${C.textDark}; resize: vertical; outline: none;
    width: 100%; box-sizing: border-box; transition: border-color 0.15s ease;
  }
  textarea.fmx-textarea:focus { border-color: ${C.blue}; }
  .fmx-type-card {
    padding: 14px 10px; border-radius: 8px; border: 1px solid ${C.border};
    background: ${C.white}; cursor: pointer; text-align: left; font-size: 13px;
    font-weight: 500; display: flex; flex-direction: column; gap: 6px;
    transition: all 0.15s ease;
  }
  .fmx-type-card:hover { background: ${C.navyTint}; border-color: ${C.navy}; }
  .fmx-tab {
    padding: 12px 16px; font-size: 13px; white-space: nowrap;
    user-select: none; border-bottom: 2px solid transparent;
    transition: all 0.15s ease; cursor: default;
  }
  .fmx-tab-active { font-weight: 700; color: ${C.orange}; border-bottom-color: ${C.orange}; }
  .fmx-tab-completed { color: ${C.blue}; cursor: pointer; }
  .fmx-tab-completed:hover { color: #4ab0cc; }
  .fmx-tab-inactive { color: ${C.textLight}; }
  .fmx-history-card {
    padding: 8px 10px; background: ${C.white}; border-radius: 6px;
    margin-bottom: 8px; border: 1px solid ${C.border};
    border-left: 3px solid ${C.blue};
  }
`;

// Per-step titles for the wizard header band + condensed chrome bar.
const WIZARD_STEP_TITLES = { 1: 'Upload', 2: 'Map fields', 3: 'Validate', 4: 'Push to FMX' };

export default function App() {
  // --- Auth state ---
  const [user, setUser] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [currentProfile, setCurrentProfile] = useState(null);
  const [allProfiles, setAllProfiles] = useState([]);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showProjectScreen, setShowProjectScreen] = useState(true);
  // When set, the landing opens straight into the admin console on this tab
  // (set by in-project admin nav / "Admin settings"; cleared on project open).
  const [landingAdminTab, setLandingAdminTab] = useState(null);
  const [passwordReset, setPasswordReset] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetMsg, setResetMsg] = useState('');

  // --- Wizard state ---
  const [history, setHistory] = useState([]);
  const [wStep, setWStep] = useState(0);
  const [schemaType, setSchemaType] = useState("");
  const [csv, setCsv] = useState(null);
  const [fileInfo, setFileInfo] = useState(null);
  const [mapping, setMapping] = useState({});
  const [transformRules, setTransformRules] = useState({});
  // customFields: array of { name: string, required: boolean }
  const [customFields, setCustomFields] = useState([]);
  const [dynamicRates, setDynamicRates] = useState([]);
  // Scheduling rate type names discovered on the FMX site (via user types) —
  // used to warn when a manually-typed bucket name matches no known rate type.
  const [rateTypeOptions, setRateTypeOptions] = useState([]);
  const [mappedRows, setMappedRows] = useState([]);
  const [certified, setCertified] = useState(false);
  // Rows the user skipped in Validate — they stay in the file but are
  // excluded from the push (and from issue counts).
  const [skippedRowIdx, setSkippedRowIdx] = useState(new Set());
  // Failed-row count from the Push step, for the chrome-bar badge.
  const [pushFailedCount, setPushFailedCount] = useState(0);
  const [aiLoading, setAiLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [pdfExtracting, setPdfExtracting] = useState(false);
  const [pdfProgress, setPdfProgress] = useState(null);
  // Tags the current csv as coming from a PDF extraction so downstream edits
  // can be captured as `corrections` for the learning loop.
  const [pdfSource, setPdfSource] = useState(null); // { runId, migrationType } | null
  // Tracks the admin-prompt run id for the CSV field-mapping call so mapping
  // edits in step 2 can be captured as `mapping_change` corrections against
  // that run (mirrors pdfSource, but for the field_mapping stage).
  const [mappingRun, setMappingRun] = useState(null); // { runId, migrationType, initialMapping } | null
  const [transformModal, setTransformModal] = useState(null); // { field, savedRule } | null
  const [memoryMatches, setMemoryMatches] = useState({});
  const [mappingSources, setMappingSources] = useState({});
  const [savedRules, setSavedRules] = useState({});
  const [depCacheMap, setDepCacheMap] = useState({}); // { [crossSheetType]: string[] } from FMX live dep cache
  // Admin-editable required-flag overrides keyed by schema_type → field_name.
  // Loaded once at app boot; admin edits in FieldRulesAdminTab call
  // setFieldOverrides so all open projects see the new precedence immediately.
  const [fieldOverrides, setFieldOverrides] = useState({});
  const [depAutoSyncing, setDepAutoSyncing] = useState(false);
  const [fmxSyncData, setFmxSyncData] = useState({ customFields: [], systemFields: [], loading: false, fromCache: undefined });
  const [checklistRefreshKey, setChecklistRefreshKey] = useState(0);
  const [xlsxWorkbook, setXlsxWorkbook] = useState(null); // { wb, typeLabel } when sheet picker active

  // Project-level routing (FMX restyle):
  //   projectTab — the lifecycle tab row (Migration · Equipment OCR · QBR builder)
  //   wizardOpen — whether the import wizard sub-flow is active inside Migration
  const [projectTab, setProjectTab] = useState('migration'); // 'migration' | 'ocr' | 'qbr'
  const [wizardOpen, setWizardOpen] = useState(false);
  // Whether the wizard's single scroll container is past the chrome (160px) —
  // drives the condensed chrome bar (design_handoff_chrome_collapse).
  const [chromeScrolled, setChromeScrolled] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showDepsModal, setShowDepsModal] = useState(false);
  // Bumped to force-remount the OCR scan tool after a "Reset scan data" so its
  // queue/history re-fetch from the now-cleared cache.
  const [scanReloadKey, setScanReloadKey] = useState(0);
  // Migration grid filters
  const [migStatusFilter, setMigStatusFilter] = useState('all');
  const [migCategoryFilter, setMigCategoryFilter] = useState('all');
  const [wizardImports, setWizardImports] = useState([]);
  const [wizardViewModal, setWizardViewModal] = useState(null); // { rec, rows } | null
  const [wizardViewLoading, setWizardViewLoading] = useState(false);
  // Scanned-equipment count for the sidebar's Attachment Field Scan note.
  const [ocrScannedCount, setOcrScannedCount] = useState(null);

  // Admin-managed feature flag. When false, the project workspace shows only
  // the Overview tab — Dependencies, Settings, and the Wizard are hidden and
  // all wizard launch points become no-ops. Admins implicitly have this.
  const canDataImport = hasFeature(currentProfile, 'data_import');

  const fileRef = useRef();

  useEffect(() => {
    // Check for password reset in URL hash
    if (window.location.hash.includes('type=recovery')) {
      setPasswordReset(true);
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoadingAuth(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setLoadingAuth(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  // Load profile + all profiles whenever the user changes
  useEffect(() => {
    if (!user?.id) { setCurrentProfile(null); setAllProfiles([]); return; }
    getCurrentProfile(user.id).then(setCurrentProfile);
    getAllProfiles().then(setAllProfiles);
  }, [user?.id]);

  const reloadProfiles = async () => {
    if (!user?.id) return;
    const [prof, all] = await Promise.all([
      getCurrentProfile(user.id),
      getAllProfiles(),
    ]);
    setCurrentProfile(prof);
    setAllProfiles(all);
  };

  const handlePasswordUpdate = async e => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { setResetMsg('Passwords do not match.'); return; }
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) { setResetMsg(error.message); return; }
    window.location.hash = '';
    setPasswordReset(false);
    setResetMsg('Password updated — please sign in.');
  };

  const fmxCustomFieldIdMap = useMemo(() => {
    const map = {};
    for (const cf of fmxSyncData.customFields || []) {
      if (cf.id && cf.name) map[cf.name] = cf.id;
    }
    return map;
  }, [fmxSyncData.customFields]);

  // Use API-driven field list when credentials are present and sync has completed
  const hasApiFields = !!selectedProject?.fmx_credentials && fmxSyncData.fromCache !== undefined;
  const baseType = schemaType ? getBaseSchemaType(schemaType) : null;
  const baseFields = useMemo(() => {
    if (!schemaType) return [];
    // Dynamic: merge live systemFields from /post-options with enrichment metadata
    if (hasApiFields && fmxSyncData.systemFields?.length && hasEnrichments(baseType)) {
      return buildFieldDefinitions(baseType, fmxSyncData.systemFields, fieldOverrides, schemaType) || [];
    }
    return []; // No fallback — API fields required
  }, [schemaType, hasApiFields, baseType, fmxSyncData.systemFields, fieldOverrides]);

  const allFields = schemaType ? [
    ...baseFields,
    // Manual custom fields only when not using API-driven field list
    ...(!hasApiFields ? customFields.filter(cf => cf.name).map(cf => ({
      name: cf.name, required: cf.required || false, type: "string", group: "Custom Fields",
    })) : []),
    // Scheduling rate buckets (Resource only): one Cost + Unit field pair per
    // named rate type. The apiKey encodes the rate name; transformRowToPayload
    // composes these into the schedulingRates: [{name, cost, unit}] array.
    // Unnamed and duplicate buckets generate no fields. Gated on baseType so a
    // late async bucket seed can't leak rate fields into another import type.
    ...(baseType === "Resource" ? (() => {
      const seen = new Set();
      return dynamicRates
        .map(n => String(n || "").trim())
        .filter(n => { if (!n || seen.has(n)) return false; seen.add(n); return true; })
        .flatMap(n => [
          { name: `${n} Cost`, required: false, type: "number", group: "Scheduling Rates", apiKey: `schedulingRates:${n}:cost` },
          { name: `${n} Unit`, required: false, type: "string", group: "Scheduling Rates", apiKey: `schedulingRates:${n}:unit` },
        ]);
    })() : []),
    // FMX custom fields from live sync (always appended; empty when no credentials)
    ...(fmxSyncData.customFields || []).map(cf => ({
      name: cf.name, required: cf.isRequired || false, type: getFieldTypeCategory(cf.fieldType), group: "FMX Custom Fields",
      isCustomField: true, customFieldId: cf.id, fieldType: cf.fieldType,
    })),
  ] : [];
  const mappedHeaders = allFields.map(f => f.name);

  const cellErrors = wStep >= 3 ? computeCellErrors(mappedRows, allFields, schemaType, depCacheMap) : {};
  const hasErrors = Object.values(cellErrors).some(v => v === "error");

  const groupedFields = {};
  allFields.forEach(f => {
    const g = f.group || "Core Fields";
    if (!groupedFields[g]) groupedFields[g] = [];
    groupedFields[g].push(f);
  });

  // Errors no longer hard-block the wizard: unresolved rows surface in the
  // Push step's hold-back flow instead (wizard v2 redesign).
  const canProceed = true;

  // Applicable entity order + completion counts for the Migration tab + tab-row
  // note. "complete" = the entity has at least one saved import — the same rule
  // the Projects list uses, so all denominators agree.
  const migApplicable = useMemo(
    () => getImportOrder(normalizeModules(selectedProject?.fmx_modules)).filter(s => !(selectedProject?.card_settings || {})[s]?.hidden),
    [selectedProject?.fmx_modules, selectedProject?.card_settings]
  );
  const migImportedSet = useMemo(() => new Set(wizardImports.map(i => i.schema_type)), [wizardImports]);
  const migTotal = migApplicable.length;
  const migDone = migApplicable.filter(s => migImportedSet.has(s)).length;
  // Total rows pushed — the sidebar customer card's tagline. Counts only the
  // newest record per entity so re-pushes don't double-count (same rule as
  // MigrationTab's Rows column).
  const migRowsPushed = useMemo(
    () => [...latestImportPerSchema(wizardImports).values()].reduce((s, r) => s + importRowsCount(r), 0),
    [wizardImports]
  );

  const handleFmxSync = async (type) => {
    if (!selectedProject?.fmx_credentials) return;
    setFmxSyncData({ customFields: [], loading: true, fromCache: undefined });
    const result = await syncFmxDataForProject(selectedProject, type);
    setFmxSyncData({ customFields: result.customFields || [], systemFields: result.systemFields || [], loading: false, fromCache: result.fromCache });
  };

  // Maps dependency cache keys to crossSheet field labels used in allFields.
  // Module-scoped caches (e.g. `request-types:maintenance`,
  // `work-task-instruction-sets:fit-inspections`) are also surfaced in
  // depCacheMap under suffixed keys like `Request Type:maintenance` so the
  // validate-step combobox can pick the right list for the current module.
  const DEP_KEY_TO_CROSS_SHEET = {
    'buildings':                  'Building',
    'equipment-types':            'Equipment Type',
    'resources':                  'Resource',
    'equipment':                  'Equipment',
    'users':                      'User',
    'request-types':              'Request Type',
    'inventory-types':            'Inventory Type',
    'inventory':                  'Inventory',
    'user-types':                 'User Type',
    'resource-types':             'Resource Type',
    'work-task-instruction-sets': 'Instruction Set',
  };

  const handleCustomFieldTypeChange = useCallback(async (fieldId, newType) => {
    const updated = (fmxSyncData.customFields || []).map(cf =>
      cf.id === fieldId ? { ...cf, fieldType: newType } : cf
    );
    setFmxSyncData(prev => ({ ...prev, customFields: updated }));
    if (selectedProject?.id && schemaType) {
      await saveFmxReferenceCache(selectedProject.id, schemaType, updated, fmxSyncData.systemFields || []);
    }
  }, [fmxSyncData, selectedProject, schemaType]);

  const loadDepCacheMap = useCallback(async () => {
    if (!selectedProject?.id) return;
    const rows = await getAllDependencyCaches(selectedProject.id);
    const map = {};
    for (const row of rows) {
      // Split off any `:slug` suffix so module-scoped caches still match
      // the base dep key (e.g. `request-types:maintenance` → `request-types`).
      const [baseKey, moduleSlug] = row.schema_type.split(':');
      const crossSheet = DEP_KEY_TO_CROSS_SHEET[baseKey];
      if (!crossSheet || !row.extra?.items?.length) continue;
      const names = row.extra.items.map(i => i.name).filter(Boolean);
      if (moduleSlug) {
        // Module-scoped entry: e.g. `Request Type:maintenance`
        map[`${crossSheet}:${moduleSlug}`] = names;
      } else {
        map[crossSheet] = names;
      }
    }
    setDepCacheMap(map);
  }, [selectedProject?.id]); // dep: selectedProject.id is the only relevant change trigger

  // Load dep cache whenever selected project changes
  useEffect(() => { loadDepCacheMap(); }, [loadDepCacheMap]);

  // Refresh import records whenever the wizard closes (back at step 1, Escape,
  // sidebar navigation) so the Migration grid reflects anything the wizard
  // persisted — not just the onImportComplete path.
  useEffect(() => {
    if (!wizardOpen && selectedProject?.id) {
      getProjectImports(selectedProject.id).then(d => setWizardImports(d || []));
    }
  }, [wizardOpen]); // eslint-disable-line

  // Load admin-editable field overrides once per session. Reloaded when the
  // admin panel saves changes via the exposed setter.
  const loadFieldOverrides = useCallback(async () => {
    const map = await getFieldOverrides();
    setFieldOverrides(map);
  }, []);
  useEffect(() => { loadFieldOverrides(); }, [loadFieldOverrides]);

  // Close the wizard / data-import surfaces when the user loses the data_import flag.
  // Runs after auth/profile load so admins revoking access on the previous
  // session take effect on the next page load (no realtime listener).
  useEffect(() => {
    if (!canDataImport && (wizardOpen || showSettingsModal || showDepsModal)) {
      setWizardOpen(false); setShowSettingsModal(false); setShowDepsModal(false);
    }
  }, [canDataImport, wizardOpen, showSettingsModal, showDepsModal]);

  // Feature-flag guard for the flag-gated tools: if a flag is flipped off
  // (or the role demoted) while the user sits on that tool, route them back
  // to the Data migration workspace — never strand someone on a screen they
  // can't see in the nav. Admins implicitly pass hasFeature.
  useEffect(() => {
    if (!currentProfile) return;
    if (projectTab === 'ocr' && !hasFeature(currentProfile, 'equipment_ocr')) setProjectTab('migration');
    if (projectTab === 'qbr' && !hasFeature(currentProfile, 'qbr_generator')) setProjectTab('migration');
    if (projectTab === 'fieldBuilder' && !hasFeature(currentProfile, 'custom_field_builder')) setProjectTab('migration');
  }, [currentProfile, projectTab]);

  // Sidebar note for the Attachment Field Scan tool: how many equipment
  // already have a cached scan on this project.
  useEffect(() => {
    if (!selectedProject?.id || !hasFeature(currentProfile, 'equipment_ocr')) { setOcrScannedCount(null); return; }
    let cancelled = false;
    listCachedEquipmentIds(selectedProject.id).then(ids => { if (!cancelled) setOcrScannedCount(ids.size); });
    return () => { cancelled = true; };
  }, [selectedProject?.id, currentProfile]);

  // Keyboard shortcuts for wizard navigation: ← prev step, → next step, Escape to Migration
  useEffect(() => {
    if (!wizardOpen) return;
    const handleKey = (e) => {
      // Skip when user is typing in an input/textarea/contenteditable
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (document.activeElement?.isContentEditable) return;
      // Skip with modifiers (avoid clobbering browser shortcuts)
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (wStep > 1) setWStep(wStep - 1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        // Only advance when the step's forward-guard is satisfied
        if (wStep === 1 && csv && !aiLoading) suggestAndAdvance();
        else if (wStep === 2) setWStep(3); // simplified: step 2→3 (goToValidate builds rows on click, keyboard just advances)
        else if (wStep === 3) setWStep(4); // unresolved errors are handled by the Push step's hold-back flow
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setWizardOpen(false);
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [wizardOpen, wStep, csv, hasErrors, certified]);

  // Fresh wizard sessions start with the chrome expanded.
  useEffect(() => { setChromeScrolled(false); }, [wizardOpen]);

  // Pre-populate the Resource wizard's scheduling-rate buckets with the site's
  // rate type names (derived from user types; cached per project). Only fills
  // empty state so manually-edited buckets are never clobbered.
  const seedSchedulingRateBuckets = (t) => {
    if (t !== "Resource" || !selectedProject?.id) return;
    syncSchedulingRateTypes(selectedProject)
      .then(names => {
        setRateTypeOptions(names);
        if (names.length > 0) setDynamicRates(prev => (prev.length > 0 ? prev : names));
      })
      .catch(() => {});
  };

  const handleSelectType = t => {
    if (!canDataImport) return;
    setSchemaType(t); setCustomFields([]); setDynamicRates([]);
    setTransformRules({}); setCertified(false); setFileInfo(null);
    setSkippedRowIdx(new Set());
    setFmxSyncData({ customFields: [], systemFields: [], loading: false, fromCache: undefined });
    setWStep(1);
    setProjectTab('migration');
    setWizardOpen(true);
    handleFmxSync(t);
    seedSchedulingRateBuckets(t);
    // Auto-refresh only the dep types this schema actually needs (parallel, schema-aware)
    if (selectedProject?.fmx_connection_verified) {
      const depKeys = getDepKeysForSchema(t); // [] for self-imports, null = all
      if (depKeys === null || depKeys.length > 0) {
        setDepAutoSyncing(true);
        fetchAllDependencies(selectedProject, () => {}, depKeys)
          .then(() => loadDepCacheMap())
          .catch(() => {})
          .finally(() => setDepAutoSyncing(false));
      }
    }
  };

  // Show a parsed {headers, rows} table as the spreadsheet preview — stays on step 1
  const previewTable = (table, info) => {
    setCsv({ headers: table.headers, rows: table.rows });
    setFileInfo({
      ...info,
      rowCount: table.rows.length,
      headerRow: table.headerRow || 0,
      droppedColumns: table.droppedColumns || 0,
    });
  };
  const parseAndPreview = (csvStr, info) => previewTable(parseCSV(csvStr), info);

  // Run AI/memory/heuristic mapping suggestions using current csv state, then advance to step 2
  const suggestAndAdvance = async () => {
    if (!csv) return;
    setAiLoading(true);
    const suggested = suggestMapping(csv.headers, allFields);
    // Route the mapping call through the admin-editable prompt system so admins
    // can tune it and we capture token usage + corrections alongside PDF runs.
    const baseType = getBaseSchemaType(schemaType);
    const fmxFieldNames = allFields.map(f => f.name);
    const mappingStart = Date.now();
    let mappingPromptRow = null;
    let mappingExamples = [];
    let mappingRunId = null;
    try {
      mappingPromptRow = await getActivePrompt(baseType, 'field_mapping');
      if (mappingPromptRow?.id) {
        mappingExamples = await getEnabledExamplesForPrompt(mappingPromptRow.id);
        const runRow = await createExtractionRun({
          projectId: selectedProject?.id,
          userId: user?.id,
          migrationType: baseType,
          stage: 'field_mapping',
          sourceFilename: fileInfo?.name || null,
          pageCount: null,
          promptId: mappingPromptRow.id,
          promptVersion: mappingPromptRow.version,
        });
        mappingRunId = runRow?.id || null;
      }
    } catch { /* non-fatal — fall back to legacy inline prompt */ }

    // Build the system prompt. If no admin prompt exists (fresh DB, migration
    // 12 not yet run), fall back to the previous inline wording so mapping
    // still works — same output contract either way.
    const systemPrompt = mappingPromptRow?.body
      ? buildSystemPrompt({
          body: mappingPromptRow.body,
          vars: {
            MIGRATION_TYPE: baseType,
            CSV_HEADERS: csv.headers,
            FMX_FIELDS: fmxFieldNames,
            SUGGESTED: suggested,
          },
          examples: mappingExamples,
        })
      : `FMX data migration. Suggest best CSV→FMX column mapping. Return ONLY valid JSON object, keys=FMX field names, values=CSV column names or null. CSV headers: ${JSON.stringify(csv.headers)}. FMX fields: ${JSON.stringify(fmxFieldNames)}. Already matched: ${JSON.stringify(suggested)}.`;

    try {
      const [aiRes, memMatches, rules] = await Promise.all([
        claudeFetch({
          max_tokens: 1000,
          system: mappingPromptRow?.body ? systemPrompt : undefined,
          messages: [{
            role: "user",
            content: mappingPromptRow?.body
              ? `CSV headers: ${JSON.stringify(csv.headers)}\nFMX fields: ${JSON.stringify(fmxFieldNames)}\nHeuristic pre-matches: ${JSON.stringify(suggested)}\n\nReturn ONLY the JSON mapping object.`
              : systemPrompt
          }]
        }).catch(() => null),
        getMappingSuggestions(schemaType, csv.headers),
        getSavedRulesForSchema(schemaType),
      ]);

      // Parse AI result
      let aiResult = {};
      if (aiRes) {
        const clean = parseClaudeText(aiRes) || "{}";
        try { aiResult = JSON.parse(clean); } catch {}
      }

      // Build final mapping: heuristic < AI < memory (confidence >= 2 wins)
      const finalMapping = { ...suggested, ...aiResult };
      const aiSuggestedFields = new Set(
        Object.entries(aiResult).filter(([, v]) => v).map(([k]) => k)
      );

      // Apply memory overrides
      Object.entries(memMatches).forEach(([sourceHeader, match]) => {
        if (match.confidence >= 2) finalMapping[match.fmxField] = sourceHeader;
      });

      // Build source attribution for badge display
      const sources = {};
      Object.entries(finalMapping).forEach(([fmxField, sourceHeader]) => {
        if (!sourceHeader) return;
        const memMatch = memMatches[sourceHeader];
        if (memMatch?.fmxField === fmxField && memMatch?.confidence >= 2) {
          sources[fmxField] = 'memory';
        } else if (aiSuggestedFields.has(fmxField)) {
          sources[fmxField] = 'ai';
        } else {
          sources[fmxField] = 'auto';
        }
      });

      setMemoryMatches(memMatches);
      setMappingSources(sources);
      setSavedRules(rules);
      setMapping(finalMapping);

      // Snapshot the AI-suggested mapping so later user edits in step 2 can
      // be diffed into `mapping_change` corrections.
      setMappingRun(mappingRunId
        ? { runId: mappingRunId, migrationType: baseType, initialMapping: { ...finalMapping } }
        : null
      );

      // Close out the extraction_run audit row with token usage.
      if (mappingRunId) {
        const usage = extractUsage(aiRes);
        completeExtractionRun(mappingRunId, {
          status: 'complete',
          resultJson: {
            mapping: finalMapping,
            fmxFieldCount: fmxFieldNames.length,
            csvHeaderCount: csv.headers.length,
            // Snapshot inputs so admin dry-runs can replay this mapping call.
            csvHeaders: csv.headers,
            fmxFieldNames,
            suggested,
          },
          durationMs: Date.now() - mappingStart,
          inputTokens: usage.inputTokens,
          outputTokens: usage.outputTokens,
          estimatedCostUsd: usage.costUsd,
        });
        if (mappingExamples?.length) incrementExampleUsage(mappingExamples.map(e => e.id));
      }
    } catch (err) {
      setMapping(suggested);
      if (mappingRunId) {
        completeExtractionRun(mappingRunId, {
          status: 'error',
          error: err?.message || String(err),
          durationMs: Date.now() - mappingStart,
        });
      }
    }
    setAiLoading(false);
    setWStep(2);
  };

  // Diffs two mapped-row arrays into per-cell `validate_edit` correction entries.
  // Keeps things reasonable by capping at 100 entries per change (bulk edits
  // shouldn't flood the corrections table).
  const diffRowsToCorrections = (prevRows, nextRows) => {
    const out = [];
    const len = Math.max(prevRows?.length || 0, nextRows?.length || 0);
    for (let i = 0; i < len && out.length < 100; i++) {
      const a = prevRows?.[i] || {};
      const b = nextRows?.[i] || {};
      const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
      for (const k of keys) {
        const av = a[k] ?? '';
        const bv = b[k] ?? '';
        if (String(av) !== String(bv)) {
          out.push({
            correctionType: 'validate_edit',
            fieldPath: k,
            rowIndex: i,
            originalValue: String(av),
            correctedValue: String(bv),
          });
          if (out.length >= 100) break;
        }
      }
    }
    return out;
  };

  // Wrapped setter for StepValidate. Fires a batch `recordCorrections` call
  // whenever the user's edits actually change cell values. Attaches to either
  // the PDF extraction run (preferred) or the CSV field_mapping run.
  const handleValidateRowsChange = (updater) => {
    setMappedRows(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      const runId = pdfSource?.runId || mappingRun?.runId;
      const migrationType = pdfSource?.migrationType || mappingRun?.migrationType;
      if (runId) {
        try {
          const entries = diffRowsToCorrections(prev, next);
          if (entries.length) {
            recordCorrections({
              extractionRunId: runId,
              migrationType,
              userId: user?.id,
              entries,
            });
          }
        } catch { /* non-fatal */ }
      }
      return next;
    });
  };

  // Wraps setMapping so that user-initiated mapping edits in Step 2 get
  // captured as `mapping_change` corrections linked to the field_mapping run.
  // StepMapFields' dropdown `onChange` calls setMapping(m => ({ ...m, [fmx]: val })),
  // so we support both functional and object updates.
  const handleMappingChange = (updater) => {
    setMapping(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      // Only capture when we have a live mapping run to attach to.
      if (mappingRun?.runId) {
        try {
          const entries = [];
          const keys = new Set([...Object.keys(prev || {}), ...Object.keys(next || {})]);
          for (const k of keys) {
            const before = prev?.[k] ?? null;
            const after = next?.[k] ?? null;
            if (before !== after) {
              entries.push({
                correctionType: 'mapping_change',
                fieldPath: k,
                rowIndex: null,
                originalValue: before,
                correctedValue: after,
              });
            }
          }
          if (entries.length) {
            recordCorrections({
              extractionRunId: mappingRun.runId,
              migrationType: mappingRun.migrationType,
              userId: user?.id,
              entries,
            });
          }
        } catch { /* non-fatal */ }
      }
      return next;
    });
  };

  const handleFileAndMap = file => {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();

    // Any non-PDF replacement invalidates the correction-capture context.
    if (ext !== "pdf") setPdfSource(null);

    if (ext === "pdf") {
      // PDF → Claude vision → {headers, rows}. Uses the admin-editable prompt
      // stored in the `prompts` table for this migration type. The resulting
      // table plugs into the existing mapping/validation/transform flow.
      setPdfExtracting(true);
      setPdfProgress({ label: "Starting…" });
      extractPdfToSheet(file, schemaType, {
        projectId: selectedProject?.id,
        userId: user?.id,
        onProgress: (label, progress) => setPdfProgress({ label, ...progress }),
      }).then(({ headers, rows, pageCount, runId }) => {
        setCsv({ headers, rows });
        setPdfSource({ runId, migrationType: schemaType });
        setFileInfo({
          type: "PDF (OCR)",
          sheetName: null,
          name: file.name,
          rowCount: rows.length,
          pageCount,
        });
      }).catch(err => {
        console.error("PDF extraction failed:", err);
        alert(`PDF extraction failed: ${err.message || err}`);
      }).finally(() => {
        setPdfExtracting(false);
        setPdfProgress(null);
      });
      return;
    }

    const reader = new FileReader();
    if (ext === "csv") {
      reader.onload = e => parseAndPreview(e.target.result, { type: "CSV", sheetName: null, name: file.name });
      reader.readAsText(file);
    } else {
      reader.onload = async e => {
        const XLSX = await import("xlsx");
        const wb = XLSX.read(new Uint8Array(e.target.result), { type: "array" });
        const typeLabel = ext === "xlsx" ? "Excel (.xlsx)" : ext === "xls" ? "Excel (.xls)" : "ODS";
        if (wb.SheetNames.length <= 1) {
          // Single sheet — proceed immediately
          const sheetName = wb.SheetNames[0];
          const ws = wb.Sheets[sheetName];
          previewTable(tableFromWorksheet(XLSX, ws), { type: typeLabel, sheetName, name: file.name });
        } else {
          // Multiple sheets — show picker
          setXlsxWorkbook({ wb, typeLabel, name: file.name });
        }
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const handleSheetSelect = async (selection) => {
    if (!xlsxWorkbook) return;
    const { wb, typeLabel, name } = xlsxWorkbook;
    const XLSX = await import("xlsx");
    setXlsxWorkbook(null);

    if (selection === '__merge__') {
      // Merge all sheets — prefix each column with "SheetName — ColumnHeader"
      // Per-sheet headers are already unique, so prefixed names never collide.
      const allHeaders = [];
      const allRows = [];
      for (const sheetName of wb.SheetNames) {
        const table = tableFromWorksheet(XLSX, wb.Sheets[sheetName]);
        const prefixed = table.headers.map(h => `${sheetName} \u2014 ${h}`);
        allHeaders.push(...prefixed);
        for (const row of table.rows) {
          const out = {};
          table.headers.forEach((h, i) => { out[prefixed[i]] = row[h]; });
          allRows.push(out);
        }
      }
      previewTable({ headers: allHeaders, rows: allRows }, { type: typeLabel, sheetName: `Merged (${wb.SheetNames.length} sheets)`, name });
    } else {
      const ws = wb.Sheets[selection];
      previewTable(tableFromWorksheet(XLSX, ws), { type: typeLabel, sheetName: selection, name });
    }
  };

  const goToValidate = () => {
    setMappedRows(buildMappedRows(csv.rows, mapping, transformRules, allFields));
    setCertified(false);
    setWStep(3);
  };

  const handleExport = async (format = "csv") => {
    const baseName = schemaType.replace(/\s+/g, "_");
    setHistory(h => [...h, { type: schemaType, rows: mappedRows.length, time: new Date().toLocaleTimeString() }]);
    if (format === "xlsx") {
      const XLSX = await import("xlsx");
      const ws = XLSX.utils.json_to_sheet(mappedRows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, schemaType.slice(0, 31));
      XLSX.writeFile(wb, `${baseName}_FMX_Import.xlsx`);
    } else {
      downloadCSV(`${baseName}_FMX_Import.csv`, mappedHeaders, mappedRows);
    }
  };

  const openTransformModal = (fieldName, savedRule = null) => {
    setTransformModal({ field: fieldName, savedRule });
  };

  // "Re-check against FMX" (Validate summary) — refreshes the dependency
  // caches the current schema needs, then reloads the dep map.
  const handleRecheckDeps = () => {
    if (!selectedProject?.fmx_connection_verified || !schemaType) return;
    const depKeys = getDepKeysForSchema(schemaType);
    setDepAutoSyncing(true);
    fetchAllDependencies(selectedProject, () => {}, depKeys)
      .then(() => loadDepCacheMap())
      .catch(() => {})
      .finally(() => setDepAutoSyncing(false));
  };

  const handleRefsLoaded = (merged) => {
    // no-op: persistentRefs state was removed; callers may still invoke this
  };

  const handleImportComplete = ({ schemaType: st, referenceValues }) => {
    setChecklistRefreshKey(k => k + 1);
    if (selectedProject?.id) {
      getProjectImports(selectedProject.id).then(d => setWizardImports(d || []));
    }
  };

  const handleResumeFromWizard = async (rec, step = 3) => {
    if (!canDataImport) return;
    const rows = await getImportRows(rec.id);
    setSchemaType(rec.schema_type);
    setMapping(rec.mapping_snapshot || {});
    setMappedRows(rows || []);
    setCustomFields([]);
    setDynamicRates([]);
    setTransformRules({});
    setCertified(false);

    setFmxSyncData({ customFields: [], systemFields: [], loading: false, fromCache: undefined });
    setWStep(step);
    setProjectTab('migration');
    setWizardOpen(true);
    handleFmxSync(rec.schema_type);
    // Re-seed rate buckets so rate columns in the saved rows still resolve to
    // fields on re-push (dynamicRates itself isn't persisted with the import).
    seedSchedulingRateBuckets(rec.schema_type);
  };

  const handleViewFromWizard = async (rec) => {
    setWizardViewLoading(true);
    setWizardViewModal({ rec, rows: [] });
    const rows = await getImportRows(rec.id);
    setWizardViewModal({ rec, rows: rows || [] });
    setWizardViewLoading(false);
  };

  const reset = () => {
    setWStep(0); setSchemaType(""); setCsv(null); setFileInfo(null); setMapping({});
    setTransformRules({}); setCustomFields([]); setDynamicRates([]);
    setMappedRows([]); setCertified(false); setSkippedRowIdx(new Set());
    setPushFailedCount(0);
    setMemoryMatches({}); setMappingSources({}); setSavedRules({});
    setMappingRun(null); setPdfSource(null);

    setFmxSyncData({ customFields: [], loading: false, fromCache: undefined });
    setWizardOpen(false);
  };

  const goToProjects = () => {
    setShowProjectScreen(true);
  };

  const handleBack = () => {
    if (wStep <= 1) {
      // At step 1, "back" closes the wizard and returns to the Migration grid
      // (wizard state is preserved so the in-progress entity can be resumed).
      setWizardOpen(false);
    } else {
      setWStep(wStep - 1);
    }
  };

  // Export a per-entity summary of this project's imports (Migration header action).
  const handleExportAll = () => {
    const headers = ['Entity type', 'Imports', 'Rows', 'Last import'];
    const bySchema = {};
    for (const imp of wizardImports) (bySchema[imp.schema_type] || (bySchema[imp.schema_type] = [])).push(imp);
    const out = Object.entries(bySchema).map(([schema, recs]) => {
      let latest = null;
      for (const r of recs) { if (!latest || new Date(r.completed_at || 0) > new Date(latest.completed_at || 0)) latest = r; }
      return {
        'Entity type': schema,
        Imports: recs.length,
        Rows: importRowsCount(latest),
        'Last import': latest?.completed_at ? new Date(latest.completed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '',
      };
    });
    downloadCSV(`${(selectedProject?.name || 'project').replace(/\s+/g, '_')}_summary.csv`, headers, out);
  };

  // "Push to FMX" header CTA → routes into the push workflow: resume an active
  // wizard, otherwise open the next not-yet-imported entity. (Pushing happens at
  // the wizard's export step; there is no separate aggregate-push backend.)
  const handlePushToFmx = () => {
    if (!canDataImport) return;
    if (schemaType) { setProjectTab('migration'); setWizardOpen(true); return; }
    const next = migApplicable.find(s => !migImportedSet.has(s));
    if (next) handleSelectType(next);
  };

  // Loading spinner
  if (loadingAuth) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui, -apple-system, sans-serif', background: C.bgPage }}>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{ width: 36, height: 36, border: `4px solid ${C.border}`, borderTopColor: C.orange, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <p style={{ marginTop: 14, color: C.textMid, fontSize: 14 }}>Loading…</p>
      </div>
    );
  }

  // Password reset modal (after clicking email link)
  if (passwordReset) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F5F5F5', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
        <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 4px 24px rgba(0,0,0,0.10)', overflow: 'hidden', width: '100%', maxWidth: 400 }}>
          <div style={{ height: 52, background: C.navy, display: 'flex', alignItems: 'center', paddingLeft: 20 }}>
            <span style={{ color: '#fff', fontWeight: 600, fontSize: 15 }}>FMX Data Migration Tool</span>
          </div>
          <form onSubmit={handlePasswordUpdate} style={{ padding: '24px 28px' }}>
            <h2 style={{ fontSize: 16, fontWeight: 600, marginTop: 0, marginBottom: 18 }}>Set new password</h2>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>New password</label>
              <input type="password" required minLength={8} value={newPassword} onChange={e => setNewPassword(e.target.value)}
                style={{ width: '100%', padding: '9px 12px', fontSize: 14, borderRadius: 6, border: '1px solid #D1D5DB', boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Confirm password</label>
              <input type="password" required minLength={8} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                style={{ width: '100%', padding: '9px 12px', fontSize: 14, borderRadius: 6, border: '1px solid #D1D5DB', boxSizing: 'border-box' }} />
            </div>
            {resetMsg && <p style={{ color: resetMsg.startsWith('Password updated') ? '#16A34A' : '#DC2626', fontSize: 13, margin: '0 0 12px' }}>{resetMsg}</p>}
            <button type="submit" style={{ width: '100%', padding: 10, fontSize: 14, fontWeight: 500, background: C.orange, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
              Update password
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Not signed in
  if (!user) return <AuthScreen />;

  // Project management screen
  if (showProjectScreen) {
    return (
      <ProjectScreen
        user={user}
        activeProjectId={selectedProject?.id || null}
        activeWizardSchema={schemaType || null}
        initialAdminTab={landingAdminTab}
        fieldOverrides={fieldOverrides}
        onFieldOverridesChanged={loadFieldOverrides}
        onSelectProject={(project, tool) => {
          const isSameProject = selectedProject?.id === project.id;

          if (!isSameProject && schemaType) {
            const confirmed = window.confirm(
              `You have an in-progress "${schemaType}" import on "${selectedProject?.name}". `
              + `Switching projects will discard this work. Continue?`
            );
            if (!confirmed) return;
          }

          if (!isSameProject) {
            reset();
            setProjectTab(tool || 'migration');
          } else if (tool) {
            // e.g. the Projects-list QBR shortcut — jump straight to that tool
            setWizardOpen(false);
            setProjectTab(tool);
          }

          setLandingAdminTab(null);
          setSelectedProject(project);
          setShowProjectScreen(false);
          getProjectImports(project.id).then(d => setWizardImports(d || []));
        }}
      />
    );
  }

  // Shell chrome pieces. In the wizard these render INSIDE its single scroll
  // container so the whole chrome condenses on scroll (chrome-collapse
  // handoff); everywhere else they stay fixed at the top of the column.
  const topBarEl = (
    <TopBar
      context={selectedProject?.name || 'FMX Implementation'}
      rightSlot={
        <UserMenu
          user={user}
          profile={currentProfile}
          onOpenProfile={() => setShowProfileModal(true)}
          onOpenAdminPanel={() => { setLandingAdminTab('users'); goToProjects(); }}
          onSignOut={handleSignOut}
        />
      }
    />
  );
  // The old ProjectTabs row (Migration · Equipment OCR · QBR builder) is
  // retired — the sidebar's TOOLS section is the only tool switcher. The
  // header crumb names the active tool; the migration header actions only
  // make sense on the Data migration tool.
  // (The Attachment Field Scan tool renders its own compact header, so no
  // crumb case for 'ocr' here.)
  const projectToolCrumb = projectTab === 'qbr' ? 'QBR decks'
    : projectTab === 'fieldBuilder' ? 'AI Form Builder' : null;
  const projectHeaderEl = (
    <ContentHeader
      crumbs={['Projects', selectedProject?.name, projectToolCrumb].filter(Boolean)}
      title={selectedProject?.name}
      subtitle={(() => {
        const owner = allProfiles.find(p => p.id === selectedProject?.user_id)?.full_name
          || allProfiles.find(p => p.id === selectedProject?.user_id)?.email || 'Unassigned';
        return `${selectedProject?.fmx_site_url ? selectedProject.fmx_site_url + ' · ' : ''}Owner: ${owner}${selectedProject?.fmx_connection_verified ? ' · FMX connected' : ''}`;
      })()}
      actions={projectTab === 'migration' ? <>
        {canDataImport && <KitButton variant="neutral" icon="gear" onClick={() => setShowSettingsModal(true)}>Settings</KitButton>}
        {canDataImport && <KitButton variant="neutral" icon="diagram-project" onClick={() => setShowDepsModal(true)}>Dependencies</KitButton>}
        <KitButton variant="neutral" icon="download" onClick={handleExportAll}>Export all</KitButton>
        {canDataImport && <KitButton variant="primary" icon="cloud-arrow-up" onClick={handlePushToFmx}>Push to FMX</KitButton>}
      </> : undefined}
    />
  );

  // Project-mode sidebar: back link, customer summary card, TOOLS section.
  // Tool notes: live entity progress, scanned count, current quarter. The
  // wizard rolls up under Data migration (projectTab stays 'migration').
  const toolNotes = {
    migration: migTotal ? `${migDone}/${migTotal}` : undefined,
    ocr: ocrScannedCount ? String(ocrScannedCount) : undefined,
    qbr: `Q${Math.floor(new Date().getMonth() / 3) + 1}`,
  };

  // Wizard navigation lives in the header band (and, while scrolled, in the
  // condensed chrome bar) — there is no fixed bottom Back/Next bar anymore.
  const wizardEntity = schemaType ? getBaseSchemaType(schemaType) : '';
  const wizardCrumbs = ['Projects', selectedProject?.name, wizardEntity].filter(Boolean);
  const wizardTitle = `${WIZARD_STEP_TITLES[wStep] || 'Import'} — ${wizardEntity}`;
  const wizardActions = (size) => (
    <>
      <KitButton variant="neutral" size={size} icon="arrow-left" onClick={handleBack}>Back</KitButton>
      {wStep === 1 && csv && (
        <KitButton variant="primary" size={size} iconAfter="arrow-right" disabled={aiLoading} onClick={suggestAndAdvance}>
          {aiLoading ? 'Analyzing columns…' : 'Map columns'}
        </KitButton>
      )}
      {wStep === 2 && (
        <KitButton variant="primary" size={size} iconAfter="arrow-right" onClick={goToValidate}>Continue to validate</KitButton>
      )}
      {wStep === 3 && (
        <KitButton variant="primary" size={size} iconAfter="arrow-right" disabled={!canProceed} onClick={() => setWStep(4)}>
          Continue to push
        </KitButton>
      )}
      {wStep === 4 && (
        <KitButton variant="secondary" size={size} icon="flag-checkered" onClick={reset}>Finish — back to workspace</KitButton>
      )}
    </>
  );
  // Live issue badges for the Validate step's header band + compact bar,
  // and the Push step's failed-count badge (reported up by StepExport).
  const wizardBadges = (() => {
    if (wStep === 4) {
      return pushFailedCount > 0
        ? <KitBadge color="red" icon="circle-exclamation">{pushFailedCount}</KitBadge>
        : null;
    }
    if (wStep !== 3) return null;
    let dep = 0, data = 0, warn = 0;
    for (const [key, v] of Object.entries(cellErrors)) {
      const ri = parseInt(key.slice(0, key.indexOf('-')), 10);
      if (skippedRowIdx.has(ri)) continue;
      if (v === 'dep_error') dep++; else if (v === 'error') data++; else warn++;
    }
    if (!dep && !data && !warn) return null;
    return (
      <>
        {dep > 0 && <KitBadge color="red" icon="link-slash">{dep}</KitBadge>}
        {data > 0 && <KitBadge color="red" icon="circle-exclamation">{data}</KitBadge>}
        {warn > 0 && <KitBadge color="orange" icon="triangle-exclamation">{warn}</KitBadge>}
      </>
    );
  })();
  const wizardActive = wizardOpen && canDataImport;

  return (
    <AppShell
      sidebar={
        <MigSidebar
          inProject
          customer={{
            name: selectedProject?.name,
            site: selectedProject?.fmx_site_url,
            done: migDone,
            total: migTotal,
            rowsPushed: migRowsPushed,
          }}
          tools={visibleTools(currentProfile).map(t => ({ ...t, note: toolNotes[t.id] }))}
          current={projectTab}
          onNavigate={(id) => { setWizardOpen(false); setProjectTab(id); }}
          onBack={goToProjects}
          onAdmin={() => { setLandingAdminTab('users'); goToProjects(); }}
          user={{ name: currentProfile?.full_name || user?.email || 'User', title: currentProfile?.role === 'admin' ? 'Admin' : 'Implementation' }}
          isAdmin={currentProfile?.role === 'admin'}
        />
      }
      topBar={wizardActive ? undefined : topBarEl}
    >
      <style>{GLOBAL_STYLES}</style>

      {showProfileModal && (
        <ProfileEditModal
          user={user}
          profile={currentProfile}
          onClose={() => setShowProfileModal(false)}
          onProfileUpdated={reloadProfiles}
        />
      )}
      {transformModal && (
        <MapRuleModal
          fieldName={transformModal.field}
          sourceColumn={mapping[transformModal.field] || null}
          csv={csv}
          mapping={mapping}
          schemaType={schemaType}
          projectId={selectedProject?.id}
          userId={user?.id}
          sourceFilename={fileInfo?.name || null}
          currentRule={transformRules[transformModal.field] || null}
          savedRule={transformModal.savedRule}
          onSave={rule => { setTransformRules(r => ({ ...r, [transformModal.field]: { ...rule, type: "formula" } })); setTransformModal(null); }}
          onRemove={() => { setTransformRules(r => { const n = { ...r }; delete n[transformModal.field]; return n; }); setTransformModal(null); }}
          onClose={() => setTransformModal(null)}
        />
      )}

      {/* Settings modal (Migration header action) */}
      {showSettingsModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: C.white, borderRadius: 12, width: '90vw', maxWidth: 760, maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: T.shadowLg }}>
            <div style={{ padding: '14px 20px', borderBottom: `1px solid ${T.borderSubtle}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <span style={{ font: `700 16px ${T.fontSans}`, color: T.ink }}>Project settings</span>
              <button onClick={() => setShowSettingsModal(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: T.inkSoft, lineHeight: 1 }}>✕</button>
            </div>
            <div style={{ overflowY: 'auto', padding: '16px 20px' }}>
              <ProjectSettingsView
                selectedProject={selectedProject}
                onProjectUpdated={(u) => setSelectedProject(u)}
                currentProfile={currentProfile}
                onProjectDeleted={() => { setShowSettingsModal(false); reset(); setSelectedProject(null); goToProjects(); }}
                onScanDataReset={() => setScanReloadKey(k => k + 1)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Dependencies modal (Migration header action) */}
      {showDepsModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: C.white, borderRadius: 12, width: '90vw', maxWidth: 900, maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: T.shadowLg }}>
            <div style={{ padding: '14px 20px', borderBottom: `1px solid ${T.borderSubtle}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <span style={{ font: `700 16px ${T.fontSans}`, color: T.ink }}>Dependencies</span>
              <button onClick={() => setShowDepsModal(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: T.inkSoft, lineHeight: 1 }}>✕</button>
            </div>
            <div style={{ overflowY: 'auto', padding: '16px 20px' }}>
              <DependenciesView projectId={selectedProject?.id} project={selectedProject} refreshKey={checklistRefreshKey} />
            </div>
          </div>
        </div>
      )}

      {/* Content header (fixed outside the wizard; inside the wizard it
          lives in its scroll container and condenses). The Attachment Field
          Scan tool brings its own compact header. */}
      {!wizardActive && projectTab !== 'ocr' && projectHeaderEl}

        {/* Migration tab — progress header + entity grid (replaces the old overview) */}
        {!wizardOpen && projectTab === 'migration' && (
          <MigrationTab
            project={selectedProject}
            imports={wizardImports}
            cardSettings={selectedProject?.card_settings || {}}
            // Known limitation: the "In progress" badge is in-memory only —
            // schemaType resets on reload and holds a single entity at a time.
            // Persisting it isn't worth it while the wizard's csv/mapping state
            // is also in-memory (a restored badge couldn't actually resume).
            activeWizardSchema={schemaType || null}
            canDataImport={canDataImport}
            hasCreds={!!selectedProject?.fmx_credentials}
            onUpload={handleSelectType}
            onResumeWizard={() => { setProjectTab('migration'); setWizardOpen(true); }}
            onView={handleViewFromWizard}
            onRepush={(rec) => handleResumeFromWizard(rec, 4)}
            onOpenOcr={hasFeature(currentProfile, 'equipment_ocr') ? () => { setWizardOpen(false); setProjectTab('ocr'); } : undefined}
            statusFilter={migStatusFilter}
            onStatusFilter={setMigStatusFilter}
            categoryFilter={migCategoryFilter}
            onCategoryFilter={setMigCategoryFilter}
          />
        )}

        {/* Existing Attachment Field Scan — gated by
            profiles.feature_permissions.equipment_ocr. Full-bleed: the tool
            owns its header, queue rail, and review desk. */}
        {!wizardOpen && projectTab === 'ocr' && hasFeature(currentProfile, 'equipment_ocr') && (
          <AttachmentFieldScan key={scanReloadKey} project={selectedProject} currentProfile={currentProfile} />
        )}

        {/* QBR builder tab — gated by profiles.feature_permissions.qbr_generator */}
        {!wizardOpen && projectTab === 'qbr' && hasFeature(currentProfile, 'qbr_generator') && (
          <div style={{ flex: 1, overflow: 'auto', background: T.bg, padding: '1.5rem 24px 2rem' }}>
            <QbrScreen project={selectedProject} currentProfile={currentProfile} />
          </div>
        )}

        {/* AI Form Builder (Custom Field Builder) — gated by
            profiles.feature_permissions.custom_field_builder */}
        {!wizardOpen && projectTab === 'fieldBuilder' && hasFeature(currentProfile, 'custom_field_builder') && (
          <div style={{ flex: 1, overflow: 'auto', background: T.bg, padding: '1.5rem 24px 2rem' }}>
            <FieldBuilderTool project={selectedProject} currentUser={user} />
          </div>
        )}

        {/* Import wizard (sub-flow of Migration). One scroll container wraps
            all the chrome so it condenses on scroll; the compact bar overlay
            keeps context + wizard actions on screen (chrome-collapse). */}
        {wizardActive && (
          <>
          <div
            onScroll={(e) => setChromeScrolled(e.target.scrollTop > 160)}
            style={{ flex: 1, minHeight: 0, overflowY: 'auto', position: 'relative', background: T.bg }}
          >
            {topBarEl}
            {projectHeaderEl}
            <WizardHeaderBand
              crumbs={wizardCrumbs}
              title={wizardTitle}
              current={wStep}
              onStep={(n) => { if (n < wStep) setWStep(n); }}
              extras={wizardBadges}
              actions={wizardActions('md')}
            />
            <div style={{ background: T.bg, padding: '1.5rem 24px 2rem' }}>

            {wStep === 1 && (
              <StepUpload
                schemaType={schemaType}
                aiLoading={aiLoading}
                fileInfo={fileInfo}
                dragOver={dragOver}
                setDragOver={setDragOver}
                fileRef={fileRef}
                handleFileAndMap={handleFileAndMap}
                fmxSyncLoading={fmxSyncData.loading}
                fmxSyncFromCache={fmxSyncData.fromCache}
                xlsxSheetNames={xlsxWorkbook ? xlsxWorkbook.wb.SheetNames : null}
                onSheetSelect={handleSheetSelect}
                csv={csv}
                setCsv={setCsv}
                pdfExtracting={pdfExtracting}
                pdfProgress={pdfProgress}
                pdfSource={pdfSource}
                currentUserId={user?.id}
              />
            )}

            {wStep === 2 && allFields.length > 0 && (
              <StepMapFields
                csv={csv}
                schemaType={schemaType}
                allFields={allFields}
                groupedFields={groupedFields}
                mapping={mapping}
                setMapping={handleMappingChange}
                transformRules={transformRules}
                dynamicRates={dynamicRates}
                setDynamicRates={setDynamicRates}
                rateTypeOptions={rateTypeOptions}
                fileInfo={fileInfo}
                openTransformModal={openTransformModal}
                memoryMatches={memoryMatches}
                mappingSources={mappingSources}
                savedRules={savedRules}
                depCacheMap={depCacheMap}
                onCustomFieldTypeChange={handleCustomFieldTypeChange}
                onRerunMapping={suggestAndAdvance}
                aiLoading={aiLoading}
              />
            )}

            {wStep === 3 && (
              <StepValidate
                mappedHeaders={mappedHeaders}
                mappedRows={mappedRows}
                setMappedRows={handleValidateRowsChange}
                cellErrors={cellErrors}
                allFields={allFields}
                schemaType={schemaType}
                depCacheMap={depCacheMap}
                depAutoSyncing={depAutoSyncing}
                projectId={selectedProject?.id}
                userId={user?.id}
                fileInfo={fileInfo}
                siteUrl={selectedProject?.fmx_site_url || ''}
                skippedRows={skippedRowIdx}
                setSkippedRows={setSkippedRowIdx}
                onRecheck={handleRecheckDeps}
              />
            )}

            {wStep === 4 && (
              <StepExport
                schemaType={schemaType}
                mappedRows={mappedRows}
                mappedHeaders={mappedHeaders}
                allFields={allFields}
                handleExport={handleExport}
                mapping={mapping}
                transformRules={transformRules}
                projectId={selectedProject?.id}
                onImportComplete={handleImportComplete}
                selectedProject={selectedProject}
                customFieldIdMap={fmxCustomFieldIdMap}
                customFieldMetadata={fmxSyncData?.customFields || []}
                systemFieldMetadata={fmxSyncData?.systemFields || []}
                fileInfo={fileInfo}
                cellErrors={cellErrors}
                skippedRows={skippedRowIdx}
                onFailedCount={setPushFailedCount}
                nextEntity={migApplicable.find(s => !migImportedSet.has(s) && s !== schemaType) || null}
                onFinish={reset}
              />
            )}

            </div>
          </div>
          <WizardCompactBar
            visible={chromeScrolled}
            crumbs={wizardCrumbs}
            title={wizardTitle}
            chip={{ label: 'Data migration', note: `${migDone}/${migTotal}` }}
            current={wStep}
            onStep={(n) => { if (n < wStep) setWStep(n); }}
            extras={wizardBadges}
            actions={wizardActions('sm')}
          />
          </>
        )}

      {/* Import row viewer modal */}
      {wizardViewModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: '#fff', borderRadius: 12,
            width: '90vw', maxWidth: 1100, maxHeight: '85vh',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
            boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
          }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <div>
                <span style={{ fontWeight: 600, fontSize: 15, color: C.navy }}>
                  {wizardViewModal.rec.import_name || wizardViewModal.rec.schema_type}
                </span>
                <span style={{ fontSize: 13, color: '#6B7280', marginLeft: 12 }}>
                  {wizardViewLoading ? 'Loading…' : `${wizardViewModal.rows.length} rows`}
                </span>
              </div>
              <button
                onClick={() => setWizardViewModal(null)}
                style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#9CA3AF', lineHeight: 1, padding: '0 4px' }}
              >
                ✕
              </button>
            </div>
            <div style={{ overflowY: 'auto', flex: 1, padding: '0 0 8px' }}>
              {wizardViewLoading ? (
                <p style={{ padding: '24px', color: '#9CA3AF', fontSize: 13 }}>Loading rows…</p>
              ) : wizardViewModal.rows.length === 0 ? (
                <p style={{ padding: '24px', color: '#9CA3AF', fontSize: 13 }}>No rows saved for this import.</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#F9FAFB', position: 'sticky', top: 0 }}>
                      {Object.keys(wizardViewModal.rows[0]).map(col => (
                        <th key={col} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: C.navy, borderBottom: '1px solid #E5E7EB', whiteSpace: 'nowrap' }}>
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {wizardViewModal.rows.map((row, ri) => (
                      <tr key={ri} style={{ borderBottom: '1px solid #F3F4F6', background: ri % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                        {Object.values(row).map((val, ci) => (
                          <td key={ci} style={{ padding: '6px 12px', color: C.textDark, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {val ?? ''}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
